/**
 * Wire contract for the BODY ZONE a defect sits in (AUT-336).
 *
 * A defect can be placed on a fixed zone of the car body (`bumper_front`,
 * `glass_windshield`, `wheel_rear_left`, …) plus optional normalized coordinates
 * INSIDE that zone. The dictionary crosses all three packages — api validates
 * writes against it, mobile draws the damage map and the report view from it,
 * admin labels defects with it — so it lives in shared, for the same reason
 * `DEFECT_SEVERITIES` does.
 *
 * Four properties of this list are load-bearing:
 *
 * 1. **The array is APPEND-ONLY.** A zone is never deleted and never renamed.
 *    Retiring a zone means removing its path from the damage-map GEOMETRY
 *    (`packages/shared/src/damage-map/zones.ts`) — the value stays here so old
 *    reports keep reading. Deleting a value would turn every historical row
 *    carrying it into an unreadable report, and a report is the document an
 *    appeal is decided on.
 *
 * 2. **The KEY ORDER IS PART OF THE CONVENTION**, not decoration:
 *    `<part>[_<longitudinal>][_<lateral>]` — part first, then `front` / `rear`,
 *    then `left` / `right` (`door_front_left`, `fender_rear_right`,
 *    `headlight_left`, `bumper_front`). A NEW ZONE MUST FOLLOW IT. Because the
 *    array is append-only, a zone named the other way round (`front_left_door`)
 *    can never be corrected afterwards: it would become a permanent exception in
 *    the i18n keys, in the map geometry and in every future grouping. Glazing is
 *    deliberately `glass_*` rather than `window_*` — `window_rear` (the rear
 *    windscreen) sitting next to `window_rear_left` (the rear DOOR's window)
 *    reads ambiguously, and the person wiring up the geometry is the one who
 *    pays for it. Human-readable names live in i18n, not in these ids.
 *
 * 3. **The array INDEX IS NOT A RANK.** Unlike the neighbouring
 *    `DEFECT_SEVERITIES`, where `indexOf` is a usable severity rank, the order
 *    here is nothing but the order zones happened to be added (append-only
 *    means new zones land at the end, not in their anatomical place). Never
 *    sort, compare or persist an index. For display order use
 *    `DEFECT_ZONE_GROUPS` or the map geometry.
 *
 * 4. **The DB column is plain text (Prisma `String?` → PG `text`), NOT a
 *    Postgres enum.** Precedent in this repo: `ReportDefect.category` /
 *    `ReportMedia.category` are `String` validated against a constant; the only
 *    PG enum is `ReportDefectSeverity` (3 values, settled). This list is
 *    expected to be refined, and a value cannot be dropped from a PG enum
 *    without rebuilding the type under a table lock — whereas a retired text
 *    value simply stops being written and keeps being readable.
 *
 * Write side is STRICT (`z.enum(DEFECT_ZONES)` → 400 on an unknown zone: the
 * damage map is built on exactly this field, so silently dropping it is worse
 * than rejecting). Read side is TOLERANT (an unknown zone coming out of the DB
 * is handed to the client as a plain string, never a 500: it is a legitimate
 * historical row written before the dictionary was refined). Hence the wire
 * type of the column is `string | null`, not `DefectZone | null`.
 */
/**
 * Every body zone a defect can be placed on. **Append-only**, and every id
 * follows `<part>[_<long>][_<lat>]` (see the file docblock); **the index is not
 * a rank**.
 *
 * 32 of these are panel / glass / light / wheel zones that get a path on the
 * damage map; the 7 `underbody` ones are offered as chips next to the map
 * instead (lift findings are not tapped on a picture). That split lives in the
 * geometry, not here — the dictionary is the source of truth for what can be
 * WRITTEN, the geometry for what can be DRAWN.
 */
export const DEFECT_ZONES = [
    // --- panels
    'bumper_front',
    'bumper_rear',
    'hood',
    'trunk_lid',
    'roof',
    'grille',
    // --- glazing
    'glass_windshield',
    'glass_rear',
    'glass_front_left',
    'glass_front_right',
    'glass_rear_left',
    'glass_rear_right',
    // --- lights
    'headlight_left',
    'headlight_right',
    'taillight_left',
    'taillight_right',
    // --- fenders
    'fender_front_left',
    'fender_front_right',
    'fender_rear_left',
    'fender_rear_right',
    // --- doors
    'door_front_left',
    'door_front_right',
    'door_rear_left',
    'door_rear_right',
    // --- side furniture
    'mirror_left',
    'mirror_right',
    'sill_left',
    'sill_right',
    // --- wheels
    'wheel_front_left',
    'wheel_front_right',
    'wheel_rear_left',
    'wheel_rear_right',
    // --- underbody (chips, not map paths)
    'underbody_front',
    'underbody_rear',
    'suspension_front',
    'suspension_rear',
    'frame_rail_left',
    'frame_rail_right',
    'exhaust',
    // AUT-380 — structural zones the checklist's body/engine-bay/trunk steps
    // point at. No map geometry: the runner offers them as chips beside the map.
    'pillar_a_left',
    'pillar_a_right',
    'pillar_b_left',
    'pillar_b_right',
    'pillar_c_left',
    'pillar_c_right',
    'strut_tower_left',
    'strut_tower_right',
    'radiator_support',
    'trunk_floor',
    'spare_wheel_well',
];
/**
 * AUT-380 — body zones with NO part on any car-scheme view: structure you only
 * see with the hood or trunk open. Pickers choose them as chips beside the
 * scheme; a report lists them with the off-body problems. Appending a drawn
 * path later just removes the id from here.
 *
 * AUT-413 — «geometry» means the five car-scheme views
 * (`damage-map/views/`), which draw the six pillars, so they left this list.
 * The legacy marketplace unfold (`damage-map/zones.ts`) still draws neither.
 */
export const DEFECT_ZONES_WITHOUT_GEOMETRY = [
    'strut_tower_left',
    'strut_tower_right',
    'radiator_support',
    'trunk_floor',
    'spare_wheel_well',
];
/**
 * Narrow an arbitrary string to a known zone. Use it on the READ path (to
 * decide whether a stored value can be localized / drawn), never to filter a
 * write — the write boundary rejects unknown zones outright.
 */
export const isDefectZone = (value) => DEFECT_ZONES.includes(value);
/**
 * Zones bucketed for display (section headers on the defect sheet, chip groups,
 * per-group counts). Every zone appears in EXACTLY ONE group and the groups
 * cover the dictionary completely — enforced by a guard test in the api package
 * (`packages/shared` has no test runner of its own; same arrangement as
 * `isHoldWindowClosed`).
 *
 * **This table is REARRANGEABLE.** It is presentational only: no group name is
 * ever persisted, so the append-only rule that binds `DEFECT_ZONES` does NOT
 * bind the grouping — a zone may be moved between groups, and a group may be
 * renamed, split or added, at the cost of a label change plus this file's guard
 * test. That is also the whole answer to «why are mirrors under `glass`»: a
 * mirror finding is in practice cracked mirror glass, a two-member group would
 * earn nothing, and if that judgement turns out wrong it is a one-line move
 * rather than a data migration.
 *
 * Grouping is NOT the report CATEGORY bucket (`exterior` / `undercarriage`),
 * which is derived separately in the map geometry.
 */
export const DEFECT_ZONE_GROUPS = {
    body: [
        'bumper_front',
        'bumper_rear',
        'hood',
        'trunk_lid',
        'roof',
        'grille',
        'fender_front_left',
        'fender_front_right',
        'fender_rear_left',
        'fender_rear_right',
        'door_front_left',
        'door_front_right',
        'door_rear_left',
        'door_rear_right',
        'sill_left',
        'sill_right',
        'pillar_a_left',
        'pillar_a_right',
        'pillar_b_left',
        'pillar_b_right',
        'pillar_c_left',
        'pillar_c_right',
        'strut_tower_left',
        'strut_tower_right',
        'radiator_support',
        'trunk_floor',
        'spare_wheel_well',
    ],
    glass: [
        'glass_windshield',
        'glass_rear',
        'glass_front_left',
        'glass_front_right',
        'glass_rear_left',
        'glass_rear_right',
        'mirror_left',
        'mirror_right',
    ],
    wheels: ['wheel_front_left', 'wheel_front_right', 'wheel_rear_left', 'wheel_rear_right'],
    lights: ['headlight_left', 'headlight_right', 'taillight_left', 'taillight_right'],
    underbody: [
        'underbody_front',
        'underbody_rear',
        'suspension_front',
        'suspension_rear',
        'frame_rail_left',
        'frame_rail_right',
        'exhaust',
    ],
};
//# sourceMappingURL=defect-zone.js.map